import type { ZoriModuleManifest } from "../../app/module-manifest";

export const INTELLIGENCE_AGENTS_PATH = "/agents";
export const INTELLIGENCE_AGENT_HUB_PATH = "/agents/:id";
export const INTELLIGENCE_AGENTES_ALIAS_PATH = "/agentes";
export const INTELLIGENCE_KNOWLEDGE_PATH = "/knowledge";
export const INTELLIGENCE_KNOWLEDGE_DETAIL_PATH = "/knowledge/:kind/:id";
export const INTELLIGENCE_MODELS_PATH = "/models";
export const INTELLIGENCE_MODEL_DETAIL_PATH = "/models/:provider/:id";
export const INTELLIGENCE_SKILLS_PATH = "/skills";
export const INTELLIGENCE_SKILL_DETAIL_PATH = "/skills/:id";
export const INTELLIGENCE_TOOLS_PATH = "/tools";
export const INTELLIGENCE_TOOL_DETAIL_PATH = "/tools/:id";
export const INTELLIGENCE_RUNTIME_PATH = "/runtime/admin";

/**
 * The Intelligence module ships the surfaces that have real mounted v2
 * contracts: agent administration (`/agents` + capability grants +
 * workspace bindings, `agents:read`/`agents:write` enforced server-side),
 * the Agent Entity Hub (`/agents/:id`),
 * skills catalog and execution (`/skills` + `/skills/:id`),
 * knowledge (`knowledge:read`/`knowledge:write`), and the models/providers/
 * capabilities catalog (`capabilities:read`). `/agentes` is an owned alias
 * canonicalizing to `/agents`.
 *
 * Recorded gaps, not faked: live agent runtime panels (skill reports,
 * files, channel/cron status) are Gateway-fed and remain on Lit
 * `/settings/channels`, `/programaciones`, `/habilidades`, `/skill-studio`;
 * five `/knowledge` list endpoints are server-side empty stubs and render
 * honest empty states.
 */
export const intelligenceModuleManifest: ZoriModuleManifest = {
  id: "intelligence",
  labelKey: "zori.modules.intelligence",
  requiredCapabilities: ["workspace.view"],
  routes: [
    {
      id: "agents",
      path: INTELLIGENCE_AGENTS_PATH,
      labelKey: "zori.intelligence.agents.page",
      context: { resourceType: "agent", queryParam: "agent" },
      requiredCapabilities: ["agents:read"],
      navGroup: "intelligence",
      icon: "agent",
    },
    {
      id: "agent-hub",
      path: INTELLIGENCE_AGENT_HUB_PATH,
      labelKey: "zori.intelligence.agents.page",
      canonicalRouteId: "intelligence:agents",
      context: { resourceType: "agent", idParam: "id" },
      requiredCapabilities: ["agents:read"],
      navHidden: true,
    },
    {
      id: "skills",
      path: INTELLIGENCE_SKILLS_PATH,
      labelKey: "zori.intelligence.skills.page",
      requiredCapabilities: ["agents:read"],
      navGroup: "intelligence",
      icon: "tool",
    },
    {
      id: "skill-detail",
      path: INTELLIGENCE_SKILL_DETAIL_PATH,
      labelKey: "zori.intelligence.skills.page",
      canonicalRouteId: "intelligence:skills",
      context: { resourceType: "skill", idParam: "id" },
      requiredCapabilities: ["agents:read"],
      navHidden: true,
    },
    {
      id: "knowledge",
      path: INTELLIGENCE_KNOWLEDGE_PATH,
      labelKey: "zori.intelligence.knowledge.page",
      requiredCapabilities: ["knowledge:read"],
      navGroup: "intelligence",
      icon: "book",
    },
    {
      id: "knowledge-detail",
      path: INTELLIGENCE_KNOWLEDGE_DETAIL_PATH,
      labelKey: "zori.intelligence.knowledge.page",
      canonicalRouteId: "intelligence:knowledge",
      context: { resourceType: "knowledge", idParam: "id" },
      requiredCapabilities: ["knowledge:read"],
      navHidden: true,
    },
    {
      id: "models",
      path: INTELLIGENCE_MODELS_PATH,
      labelKey: "zori.intelligence.models.page",
      requiredCapabilities: ["capabilities:read"],
      navGroup: "intelligence",
      icon: "chip",
    },
    {
      id: "model-detail",
      path: INTELLIGENCE_MODEL_DETAIL_PATH,
      labelKey: "zori.intelligence.models.detail.page",
      canonicalRouteId: "intelligence:models",
      context: { resourceType: "model", idParam: "id" },
      requiredCapabilities: ["capabilities:read"],
      navHidden: true,
    },
    {
      id: "tools",
      path: INTELLIGENCE_TOOLS_PATH,
      labelKey: "zori.intelligence.tools.page",
      // The Tools catalog and executions are backed by actions.definitions /
      // actions.executions — the API enforces `actions:read`, and the manifest
      // must declare the same capability or read-only views get a false gate.
      requiredCapabilities: ["actions:read"],
      navGroup: "intelligence",
      icon: "tool",
    },
    {
      id: "tool-detail",
      path: INTELLIGENCE_TOOL_DETAIL_PATH,
      labelKey: "zori.intelligence.tools.page",
      canonicalRouteId: "intelligence:tools",
      context: { resourceType: "tool", idParam: "id" },
      requiredCapabilities: ["actions:read"],
      navHidden: true,
    },
    {
      id: "runtime-admin",
      path: INTELLIGENCE_RUNTIME_PATH,
      labelKey: "zori.intelligence.runtime.page",
      // Normalized channel accounts, MCP endpoints, and plugin assets —
      // the FK targets binding editors pick from.
      requiredCapabilities: ["capabilities:read"],
      navGroup: "intelligence",
      icon: "chip",
    },
    {
      id: "agentes-alias",
      path: INTELLIGENCE_AGENTES_ALIAS_PATH,
      labelKey: "zori.intelligence.agents.page",
      canonicalRouteId: "intelligence:agents",
      requiredCapabilities: ["agents:read"],
      navHidden: true,
    },
  ],
};
